import { t } from 'i18next';
import React, { useMemo } from 'react';
import { useRecoilState } from 'recoil';
import captureEvent from 'src/app/lib/gtm-functions';
import {
  conversationsAtom,
  explanationPinAtom,
  frameParamsAtom,
  pinAtom,
} from 'src/app/state/atoms';
import { ReactComponent as PinFillSVG } from 'src/assets/images/pin-fill.svg';
import { ReactComponent as PinSVG } from 'src/assets/images/pin.svg';
import { v4 as uuidv4 } from 'uuid';

type PinButtonPropType = {
  label: string;
  backgroundColor?: string;
  providedPinId?: string; //If pinId provided, it will be used as the unique identifier for the pin
  group?: string | number; // If there is a group of pins.
};

function PinButton(props: PinButtonPropType) {
  const [pinState, setPins] = useRecoilState(pinAtom);
  const [exPins, setExPins] = useRecoilState(explanationPinAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);
  const [conversations] = useRecoilState(conversationsAtom);
  const { label, backgroundColor, providedPinId, group } = props;

  const pinId = useMemo(() => {
    return providedPinId ?? uuidv4();
  }, [providedPinId]);

  const handlePinClick = () => {
    console.log('handlePinClick + pinId: ', pinId);
    const newPins = new Set(pinState.pins);
    for (const pin of pinState.pins ?? []) {
      if (pin.pinId === pinId) {
        newPins.delete(pin);
        setPins({ pins: Array.from(newPins) });

        // Delete unwanted explanation pins
        const filteredExPins = exPins.filter((_pin) => _pin !== pinId);
        setExPins([...filteredExPins]);

        // Telemetry: Click Pin Icon
        captureEvent({
          event_action: 'Click Pin Icon',
          event_label: `${newPins.size}`,
          element_status: 'unpinned',
          element_content: 'Text',
          conversation_id: conversations.data.id,
          mlm_book_code: frameParams?.book_code ?? 'N/A',
          app_id: 'MLMAITutor',
          exercise_id: frameParams?.exercise_id,
          user_role: frameParams?.['user-role'],
          person_id_type: frameParams?.person_id_type,
          login_session_id: frameParams?.login_session_id,
        });
        return;
      }
    }
    newPins.add({ pinId: pinId, content: label, group: group });
    setPins({ pins: Array.from(newPins) });

    // Add new explanation pins
    setExPins([...exPins, pinId]);

    // Telemetry: Click Pin Icon
    captureEvent({
      event_action: 'Click Pin Icon',
      event_label: `${newPins.size}`,
      element_status: 'pinned',
      element_type: 'Text',
      conversation_id: conversations.data.id,
      mlm_book_code: frameParams?.book_code ?? 'N/A',
      app_id: 'MLMAITutor',
      exercise_id: frameParams?.exercise_id,
      user_role: frameParams?.['user-role'],
      person_id_type: frameParams?.person_id_type,
      login_session_id: frameParams?.login_session_id,
    });
    return;
  };

  const isChecked = () => {
    for (const pin of pinState.pins ?? []) {
      if (pin.pinId === pinId) {
        return true;
      }
    }
    return false;
  };

  const bgColor = backgroundColor ?? 'black';
  const isPinned = isChecked();
  const ariaLabel = isPinned ? t('UNPIN') : t('PIN')

  return (
    <button onClick={handlePinClick} aria-label={ariaLabel} type='button'>
      {isChecked() ? (
        <span className='group'>
          <PinFillSVG className='inline-block mr-space-2' fill={bgColor} aria-hidden={true} />
          <div className='relative top-4 scale-0 w-12 rounded bg-gray-800 p-space-2 text-xs text-white group-hover:scale-100'>
            {t('UNPIN')}
          </div>
        </span>
      ) : (
        <span className='group'>
          <PinSVG className='inline-block mr-space-2' fill={bgColor} aria-hidden={true} />
          <div className='relative top-4 scale-0 w-12 rounded bg-gray-800 p-space-2 text-xs text-white group-hover:scale-100'>
            {t('PIN')}
          </div>
        </span>
      )}
    </button>
  );
}

export default PinButton;
